import React from 'react';
import { Button, Alert } from '@/components/ui';
import { Task, TaskType } from '../types/Task';
import { ForemanArea } from '../types/ForemanArea';
import { Project } from '../types/Project';
import ResponsiveModal from '../features/quantified-tracking/components/ResponsiveModal';
import GenericTaskModal from './GenericTaskModal';
import RoutineTaskModal from './RoutineTaskModal';
import QuantifiedTaskModal from '../features/quantified-tracking/components/taskModals/TaskModal';
import { ContractArea } from '../types/ContractArea';
import { RoutineTask } from '../types/RoutineTask';
import type { QuantifiedInitialData, RoutineInitialData } from '../features/work-id/utils/workIdToTaskInitialData';

interface TaskModalProps {
  visible: boolean;
  onClose: () => void;
  task?: Task;
  onTaskSaved: (updatedTask: Task, options?: { isNew?: boolean; keepOpen?: boolean }) => void;
  foremanAreas: ForemanArea[];
  taskType?: TaskType;
  project?: Project;
  initialContractAreas?: ContractArea[];
  defaultStatus?: string;
  initialDataFromWorkId?: QuantifiedInitialData | RoutineInitialData;
}

const TaskModal: React.FC<TaskModalProps> = ({
  visible,
  onClose,
  task,
  onTaskSaved,
  foremanAreas,
  taskType = 'Quantified',
  project,
  initialContractAreas = [],
  defaultStatus,
  initialDataFromWorkId
}) => {
  // Route to the appropriate task modal based on type
  switch (taskType) {
    case 'Overhead':
      return (
        <GenericTaskModal
          visible={visible}
          onClose={onClose}
          task={task}
          onTaskSaved={onTaskSaved}
          projectId={project?.id || task?.projectId || 0}
          defaultStatus={defaultStatus}
        />
      );

    case 'Routine':
      return (
        <RoutineTaskModal
          visible={visible}
          onClose={onClose}
          task={task as unknown as RoutineTask}
          onTaskSaved={(routineTask) => onTaskSaved(routineTask as unknown as Task)}
          project={project}
          defaultStatus={defaultStatus}
          initialDataFromWorkId={initialDataFromWorkId as RoutineInitialData | undefined}
        />
      );

    case 'Quantified':
      return (
        <QuantifiedTaskModal
          taskType="Quantified"
          visible={visible}
          onClose={onClose}
          task={task}
          onTaskSaved={onTaskSaved}
          foremanAreas={foremanAreas}
          project={project}
          initialContractAreas={initialContractAreas}
          defaultStatus={defaultStatus}
          initialDataFromWorkId={initialDataFromWorkId as QuantifiedInitialData | undefined}
        />
      );
    
    case 'Internal':
      // For now, show a placeholder modal for unsupported types
      return (
        <ResponsiveModal
          isOpen={visible}
          onClose={onClose}
          title={`${taskType} Task`}
          footer={
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
            >
              Close
            </Button>
          }
        >
          <Alert variant="info">
            {taskType} tasks will be supported in a future update.
          </Alert>
        </ResponsiveModal>
      );

    default:
      // Fallback to Generic task modal for unknown types
      return (
        <GenericTaskModal
          visible={visible}
          onClose={onClose}
          task={task}
          onTaskSaved={onTaskSaved}
          projectId={project?.id || task?.projectId || 0}
          defaultStatus={defaultStatus}
        />
      );
  }
};

export default TaskModal; 